/**
 * Admin winback control surface. Mirrors `app/owner/subscriptions/winback/index.tsx`
 * with admin impersonation via ?ownerId=.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import Card from '../../../../../src/components/Card';
import Button from '../../../../../src/components/Button';
import Input from '../../../../../src/components/Input';
import { useToast } from '../../../../../src/components/Toast';
import ContentContainer from '../../../../../src/components/ContentContainer';
import { useTheme } from '../../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../../../src/utils/theme';
import { makeOwnerApi } from '../../../../../src/api/adminAsOwner';
import { titleCase } from '../../../../../src/utils/format';
import { SMS_UI_ENABLED, PUSH_UI_ENABLED } from '../../../../../src/config/features';

interface LapsedCustomer { user_id: string; email: string; name: string; last_seen_at: string; total_washes: number; lifetime_cents: number }
interface Campaign {
  id: string; name: string; lapsedDays: number; isActive: boolean;
  runFrequencyDays: number; lastRunAt: string | null;
  voucherTemplateJson: any; emailSubject: string; emailBody: string;
}

const DEFAULT_SUBJECT = 'We miss you!';
const DEFAULT_BODY = "It's been a while. Come back and we'll cover a portion of your next wash on us.";

export default function AdminWinbackScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { id: ownerId } = useLocalSearchParams<{ id: string }>();
  const ownerApi = useMemo(() => makeOwnerApi(ownerId ?? ''), [ownerId]);

  const [tab, setTab] = useState<'cohort' | 'campaigns'>('cohort');
  const [days, setDays] = useState(60);
  const [customers, setCustomers] = useState<LapsedCustomer[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stats, setStats] = useState<{ campaigns: { campaignId: string; sends: number; delivered: number; redeemed: number; revenueCents: number }[]; manual: { sends: number; redeemed: number; revenueCents: number } } | null>(null);

  const [offerType, setOfferType] = useState<'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit'>('percent');
  const [channels, setChannels] = useState<{ email: boolean; push: boolean; sms: boolean }>({ email: true, push: true, sms: false });
  const [percentOff, setPercentOff] = useState('25');
  const [freeMinutes, setFreeMinutes] = useState('10');
  // free_minutes scope: self-serve bays and/or vacuums (default self-serve on).
  const [minutesSelfServe, setMinutesSelfServe] = useState(true);
  const [minutesVacuum, setMinutesVacuum] = useState(false);
  // Free vending credit in dollars ($0.25–$100 → freeVendCents); scope forced to vending server-side.
  const [freeVendAmount, setFreeVendAmount] = useState('2');
  // Wash credit in dollars ($1–$500 → creditCents); deposited directly at send.
  const [creditAmount, setCreditAmount] = useState('10');
  const [bayType, setBayType] = useState<'automatic' | 'self-serve' | null>('automatic');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [expiresInDays, setExpiresInDays] = useState('30');
  const [sending, setSending] = useState(false);

  /** Build the voucher template from the offer form; returns null + toasts on invalid input. */
  const buildVoucherTemplate = () => {
    if (offerType === 'percent') {
      const pct = parseInt(percentOff);
      if (!pct || pct < 1 || pct > 99) {
        toast.show({ type: 'error', title: 'Error', message: 'Percent must be 1–99 (use Free wash for 100%)' });
        return null;
      }
      return { percentOff: pct, bayType, tierName: null, locationId: null, freeMinutes: null, expiresInDays: parseInt(expiresInDays) || 30 };
    }
    if (offerType === 'free_minutes') {
      const mins = parseInt(freeMinutes);
      if (!mins || mins < 1 || mins > 240) {
        toast.show({ type: 'error', title: 'Error', message: 'Free minutes must be 1–240' });
        return null;
      }
      if (!minutesSelfServe && !minutesVacuum) {
        toast.show({ type: 'error', title: 'Error', message: 'Pick self-serve bays, vacuums, or both.' });
        return null;
      }
      // appliesTo carries the self-serve/vacuum split (no tiers, no vending).
      return { appliesTo: { tierIds: [], selfServe: minutesSelfServe, vacuum: minutesVacuum }, locationId: null, freeMinutes: mins, expiresInDays: parseInt(expiresInDays) || 30 };
    }
    if (offerType === 'free_vend') {
      const cents = Math.round((parseFloat(freeVendAmount) || 0) * 100);
      if (cents < 25 || cents > 10000) {
        toast.show({ type: 'error', title: 'Error', message: 'Vending credit must be $0.25–$100' });
        return null;
      }
      // Server forces the vending-only scope for freeVendCents offers.
      return { freeVendCents: cents, locationId: null, freeMinutes: null, expiresInDays: parseInt(expiresInDays) || 30 };
    }
    if (offerType === 'credit') {
      const cents = Math.round((parseFloat(creditAmount) || 0) * 100);
      if (cents < 100 || cents > 50000) {
        toast.show({ type: 'error', title: 'Error', message: 'Credit must be $1–$500' });
        return null;
      }
      // Deposited DIRECTLY into the customer's balance at send — no code to redeem.
      return { creditCents: cents, locationId: null, freeMinutes: null, expiresInDays: parseInt(expiresInDays) || 30 };
    }
    return { percentOff: 100, bayType, tierName: null, locationId: null, freeMinutes: null, expiresInDays: parseInt(expiresInDays) || 30 };
  };

  /** Human description of a campaign's stored offer. */
  const describeOffer = (t: any): string => {
    if (!t) return 'offer';
    if (t.creditCents) return `$${(t.creditCents / 100).toFixed(2)} credit (deposited at send)`;
    if (t.freeVendCents) return `$${(t.freeVendCents / 100).toFixed(2)} free vending credit`;
    if (t.freeMinutes) return `${t.freeMinutes} free minutes`;
    if (t.percentOff === 100) return 'Free wash';
    return `${t.percentOff}% off`;
  };

  const [campaignName, setCampaignName] = useState('Lapsed customer winback');
  const [autoLapsedDays, setAutoLapsedDays] = useState('60');
  const [autoRunFrequencyDays, setAutoRunFrequencyDays] = useState('7');
  const [autoCooldownDays, setAutoCooldownDays] = useState('30');

  useEffect(() => { if (ownerId) { fetchCohort(); fetchCampaigns(); } }, [ownerId]);
  useEffect(() => { if (ownerId) fetchCohort(); }, [days, ownerId]);

  async function fetchCohort() {
    try {
      const res = await ownerApi.get<{ customers: LapsedCustomer[] }>(`/api/winback/lapsed-customers?days=${days}`);
      setCustomers(res.customers);
      setSelected(new Set());
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  }
  async function fetchCampaigns() {
    try {
      const res = await ownerApi.get<{ campaigns: Campaign[] }>('/api/winback/campaigns');
      setCampaigns(res.campaigns);
      ownerApi.get<typeof stats>('/api/winback/stats').then(d => setStats(d as any)).catch(() => {});
    } catch { /* silent */ }
  }

  const toggleSelect = (uid: string) => {
    setSelected(prev => { const s = new Set(prev); if (s.has(uid)) s.delete(uid); else s.add(uid); return s; });
  };

  const handleSend = async (mode: 'one_off' | 'batch') => {
    const userIds = Array.from(selected);
    if (userIds.length === 0) return;
    const voucherTemplate = buildVoucherTemplate();
    if (!voucherTemplate) return;
    try {
      setSending(true);
      const res = await ownerApi.post<{ succeeded: number; failed: number; total: number }>('/api/winback/send', {
        mode, userIds,
        voucherTemplate,
        emailSubject: subject, emailBody: body,
        channels,
      });
      toast.show({ type: 'success', title: `Sent ${res.succeeded} of ${res.total}` });
      setSelected(new Set());
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSending(false); }
  };

  const handleCreateCampaign = async () => {
    const voucherTemplate = buildVoucherTemplate();
    if (!voucherTemplate) return;
    try {
      setSending(true);
      await ownerApi.post('/api/winback/campaigns', {
        name: campaignName,
        lapsedDays: parseInt(autoLapsedDays) || 60,
        voucherTemplate,
        emailSubject: subject, emailBody: body,
        channels,
        isActive: true,
        runFrequencyDays: parseInt(autoRunFrequencyDays) || 7,
        recipientCooldownDays: parseInt(autoCooldownDays) || 30,
      });
      toast.show({ type: 'success', title: 'Automated campaign created' });
      await fetchCampaigns();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSending(false); }
  };

  const handleRunNow = async (id: string) => {
    try {
      const res = await ownerApi.post<{ succeeded: number; failed: number; skipped: number }>(`/api/winback/campaigns/${id}/run-now`, {});
      toast.show({ type: 'success', title: 'Ran campaign', message: `Sent ${res.succeeded}, skipped ${res.skipped}` });
      await fetchCampaigns();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    try {
      await ownerApi.delete(`/api/winback/campaigns/${id}`);
      toast.show({ type: 'success', title: 'Campaign deactivated' });
      await fetchCampaigns();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
        <Text style={styles.adminBanner}>Admin acting on behalf of owner {ownerId}</Text>

        <View style={styles.tabRow}>
          {(['cohort', 'campaigns'] as const).map(t => (
            <TouchableOpacity key={t} onPress={() => setTab(t)} style={[styles.tab, tab === t && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
              <Text style={[styles.tabText, tab === t && { color: '#fff' }]}>{t === 'cohort' ? 'Send Now' : 'Automated Rules'}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <Card style={styles.formCard}>
          <Text style={styles.sectionTitle}>Offer</Text>
          <Text style={styles.fieldLabel}>Offer type</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
            {([['percent', '% off'], ['free_wash', 'Free wash'], ['free_minutes', 'Free minutes'], ['free_vend', 'Free vending credit ($)'], ['credit', 'Credit $']] as const).map(([val, label]) => (
              <TouchableOpacity key={val} onPress={() => setOfferType(val)} style={[styles.chip, offerType === val && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                <Text style={[styles.chipText, offerType === val && { color: '#fff' }]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {offerType === 'percent' && (
            <Input label="Percent off (1–99)" value={percentOff} onChangeText={setPercentOff} keyboardType="number-pad" />
          )}
          {offerType === 'free_minutes' && (
            <>
              <Input label="Free minutes (1–240)" value={freeMinutes} onChangeText={setFreeMinutes} keyboardType="number-pad" />
              <Text style={styles.fieldLabel}>Valid on</Text>
              <TouchableOpacity style={styles.checkRow} onPress={() => setMinutesSelfServe(v => !v)}>
                <View style={[styles.checkbox, minutesSelfServe && styles.checkboxActive]}>
                  {minutesSelfServe && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Self-serve bays</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.checkRow} onPress={() => setMinutesVacuum(v => !v)}>
                <View style={[styles.checkbox, minutesVacuum && styles.checkboxActive]}>
                  {minutesVacuum && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>Vacuums</Text>
              </TouchableOpacity>
              <View style={{ height: spacing.sm }} />
            </>
          )}
          {offerType === 'free_vend' && (
            <>
              <Input label="Vending credit ($0.25–$100)" value={freeVendAmount} onChangeText={setFreeVendAmount} keyboardType="decimal-pad" />
              <Text style={styles.hintText}>Vending machines only — scope is forced server-side.</Text>
            </>
          )}
          {offerType === 'credit' && (
            <>
              <Input label="Credit ($1–$500)" value={creditAmount} onChangeText={setCreditAmount} keyboardType="decimal-pad" />
              <Text style={styles.hintText}>Deposited straight into their balance when the campaign sends — no code to redeem.</Text>
            </>
          )}
          {(offerType === 'percent' || offerType === 'free_wash') && (
            <>
              <Text style={styles.fieldLabel}>Valid on</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md }}>
                {([[null, 'Any bay'], ['automatic', 'Automatic'], ['self-serve', 'Self-serve']] as const).map(([val, label]) => (
                  <TouchableOpacity key={label} onPress={() => setBayType(val)} style={[styles.chip, bayType === val && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                    <Text style={[styles.chipText, bayType === val && { color: '#fff' }]}>{label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
          <Text style={styles.fieldLabel}>Send via</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.xs }}>
            {([['email', 'Email'], ...(PUSH_UI_ENABLED ? [['push', 'Push'] as const] : []), ...(SMS_UI_ENABLED ? [['sms', 'Text'] as const] : [])] as ReadonlyArray<readonly [('email' | 'push' | 'sms'), string]>).map(([key, label]) => (
              <TouchableOpacity
                key={key}
                onPress={() => setChannels(prev => ({ ...prev, [key]: !prev[key] }))}
                style={[styles.chip, channels[key] && { backgroundColor: colors.primary, borderColor: colors.primary }]}
              >
                <Text style={[styles.chipText, channels[key] && { color: '#fff' }]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {channels.sms && (
            <Text style={{ fontSize: fontSize.xs, color: colors.warning, marginBottom: spacing.md }}>
              Texts cost extra: ~1¢ per message, billed weekly at actual carrier cost. Only customers who opted in to texts receive them.
            </Text>
          )}
          <Input label="Expires in (days)" value={expiresInDays} onChangeText={setExpiresInDays} keyboardType="number-pad" />
          <Input label="Email subject" value={subject} onChangeText={setSubject} />
          <Input label="Email body" value={body} onChangeText={setBody} multiline numberOfLines={4} maxLength={5000} />
        </Card>

        {tab === 'cohort' ? (
          <>
            {stats && stats.manual.sends > 0 && (
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.sm }}>
                Manual winbacks so far: sent {stats.manual.sends} · won back {stats.manual.redeemed} ({Math.round((stats.manual.redeemed / stats.manual.sends) * 100)}%) · ${(stats.manual.revenueCents / 100).toFixed(2)} revenue
              </Text>
            )}
            <Card style={styles.formCard}>
              <Text style={styles.sectionTitle}>Cohort</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm }}>
                {[30, 60, 90, 180].map(n => (
                  <TouchableOpacity key={n} onPress={() => setDays(n)} style={[styles.chip, days === n && { backgroundColor: colors.primary, borderColor: colors.primary }]}>
                    <Text style={[styles.chipText, days === n && { color: '#fff' }]}>{n} days lapsed</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </Card>
            <Card style={styles.formCard}>
              <Text style={styles.sectionTitle}>{customers.length} lapsed customers</Text>
              {customers.slice(0, 100).map(c => (
                <TouchableOpacity key={c.user_id} onPress={() => toggleSelect(c.user_id)} style={[styles.customerRow, selected.has(c.user_id) && { backgroundColor: colors.primaryBg }]}>
                  <Ionicons name={selected.has(c.user_id) ? 'checkbox' : 'square-outline'} size={20} color={selected.has(c.user_id) ? colors.primary : colors.textLight} />
                  <View style={{ flex: 1, marginLeft: spacing.sm }}>
                    <Text style={styles.customerName}>{titleCase(c.name)}</Text>
                    <Text style={styles.customerSub}>{c.email} · last seen {new Date(c.last_seen_at).toLocaleDateString()}</Text>
                  </View>
                  <Text style={styles.customerStats}>${((c.lifetime_cents ?? 0) / 100).toFixed(0)} · {c.total_washes} washes</Text>
                </TouchableOpacity>
              ))}
              {customers.length === 0 && <Text style={styles.hintText}>No lapsed customers at this threshold.</Text>}
            </Card>
            <View style={{ flexDirection: 'row', gap: spacing.sm }}>
              <Button title={`Send to ${selected.size}`} onPress={() => handleSend(selected.size === 1 ? 'one_off' : 'batch')} loading={sending} disabled={selected.size === 0} size="lg" style={{ flex: 1 }} />
              <Button title="Select all" variant="secondary" onPress={() => setSelected(new Set(customers.map(c => c.user_id)))} />
            </View>
          </>
        ) : (
          <>
            <Card style={styles.formCard}>
              <Text style={styles.sectionTitle}>New Automated Campaign</Text>
              <Input label="Campaign name" value={campaignName} onChangeText={setCampaignName} />
              <Input label="Lapsed days" value={autoLapsedDays} onChangeText={setAutoLapsedDays} keyboardType="number-pad" />
              <Input label="Re-check every (days)" value={autoRunFrequencyDays} onChangeText={setAutoRunFrequencyDays} keyboardType="number-pad" />
              <Input label="Per-recipient cooldown (days)" value={autoCooldownDays} onChangeText={setAutoCooldownDays} keyboardType="number-pad" />
              <Button title="Create Automated Campaign" onPress={handleCreateCampaign} loading={sending} style={{ marginTop: spacing.sm }} />
            </Card>

            {campaigns.length > 0 && (
              <Card style={styles.formCard}>
                <Text style={styles.sectionTitle}>Active campaigns</Text>
                {campaigns.map(c => (
                  <View key={c.id} style={styles.campaignRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.customerName}>{c.name}</Text>
                      <Text style={styles.customerSub}>
                        {describeOffer(c.voucherTemplateJson)} · {c.lapsedDays} days lapsed · every {c.runFrequencyDays} days
                        {(() => {
                          const st = stats?.campaigns.find(x => x.campaignId === c.id);
                          if (!st || st.sends === 0) return '';
                          const pct = Math.round((st.redeemed / st.sends) * 100);
                          return `\nsent ${st.sends} · won back ${st.redeemed} (${pct}%) · $${(st.revenueCents / 100).toFixed(2)} revenue`;
                        })()}
                      </Text>
                      <Text style={styles.customerSub}>Last run: {c.lastRunAt ? new Date(c.lastRunAt).toLocaleString() : 'never'}</Text>
                    </View>
                    <View style={{ gap: spacing.xs }}>
                      <Button title="Run now" size="sm" onPress={() => handleRunNow(c.id)} />
                      <Button title="Deactivate" size="sm" variant="outline" onPress={() => handleDeleteCampaign(c.id)} />
                    </View>
                  </View>
                ))}
              </Card>
            )}
          </>
        )}
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginBottom: spacing.md },
  tabRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  tab: { flex: 1, alignItems: 'center', paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  tabText: { fontSize: fontSize.sm, color: c.text },
  chip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium as any, color: c.textSecondary, marginBottom: spacing.xs },
  chipText: { fontSize: fontSize.sm, color: c.text },
  customerRow: { flexDirection: 'row', alignItems: 'center', padding: spacing.sm, borderRadius: borderRadius.sm, marginBottom: spacing.xs },
  customerName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any, color: c.text },
  customerSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  customerStats: { fontSize: fontSize.xs, color: c.textSecondary },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic', textAlign: 'center', padding: spacing.md },
  campaignRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  adminBanner: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginBottom: spacing.sm },
  checkRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm },
  checkbox: { width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: c.border, justifyContent: 'center', alignItems: 'center' },
  checkboxActive: { backgroundColor: c.primary, borderColor: c.primary },
  checkmark: { color: c.white, fontSize: 12, fontWeight: fontWeight.bold as any },
  checkLabel: { fontSize: fontSize.sm, color: c.text, marginLeft: spacing.sm, flexShrink: 1 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
